import { RoomModel } from './room.model';
import { HashMap, TranslocoService } from '@jsverse/transloco';
import { Currency } from 'src/app/model/wallet/currency';
import { CurrencyPipe } from '@angular/common';
import { RoomResultStatus, RoomStatus } from 'src/app/shared/types/room-status';
import { RoomDisplayResult } from 'src/app/shared/types/room-display-result';
import { RoomError } from 'src/app/shared/errors/room-error';
import { isNullOrUndefined } from 'src/app/shared/utils/app.utils';
import { Round } from 'src/app/shared/types/round';
import { GameId } from '../game/game-id';
import { amountForCurrency } from '../wager/wager';

export class Room {
  constructor(
    readonly model: RoomModel,
    readonly id: string,
    readonly path: string,
  ) {}

  gameId(): GameId {
    return this.model.gameId;
  }

  logicVersion(): number {
    return this.model.logicVersion;
  }

  status(): RoomStatus {
    if (this.isProcessing()) {
      return RoomStatus.Processing;
    } else if (this.model.result === RoomDisplayResult.Cancelled) {
      return RoomStatus.Cancelled;
    } else if (this.model.result === RoomDisplayResult.Draw) {
      return RoomStatus.Draw;
    } else if (this.model.result === RoomDisplayResult.Lose) {
      return RoomStatus.Lost;
    } else if (this.model.result === RoomDisplayResult.Win) {
      return RoomStatus.Won;
    } else if (this.model.result === RoomDisplayResult.InProgress) {
      if (!this.hasOpponent()) {
        if (this.isExpired()) {
          return RoomStatus.Cancelled;
        }
        if (this.isRoundNotStarted()) {
          if (this.isStartRoundDeadlineExpired()) {
            return RoomStatus.Cancelled;
          }
          return RoomStatus.Start;
        }
        if (!this.isRoundFinished() && !this.isRoundDeadlineExpired()) {
          return RoomStatus.Finish;
        }
        return RoomStatus.Open;
      } else if (
        !this.isStartRoundDeadlineExpired() &&
        this.isRoundNotStarted() &&
        this.hasOpponentStarted() &&
        !this.hasOpponentRoundExpired()
      ) {
        return RoomStatus.Start;
      } else if (
        !this.isStartRoundDeadlineExpired() &&
        this.isRoundNotStarted() &&
        this.hasOpponentNotStarted()
      ) {
        return RoomStatus.Start;
      } else if (
        this.isStartRoundDeadlineExpired() &&
        this.isRoundNotStarted() &&
        this.hasOpponentNotStarted()
      ) {
        return RoomStatus.Cancelled;
      } else if (
        this.isRoundNotStarted() &&
        this.isStartRoundDeadlineExpired() &&
        this.hasOpponentStarted() &&
        this.hasOpponentRoundExpired()
      ) {
        return RoomStatus.Cancelled;
      } else if (
        this.isRoundNotStarted() &&
        !this.isStartRoundDeadlineExpired() &&
        this.hasOpponentStarted() &&
        this.hasOpponentRoundExpired()
      ) {
        return RoomStatus.Start;
      } else if (this.isRoundStarted() && !this.isRoundDeadlineExpired()) {
        return RoomStatus.Finish;
      } else if (
        this.isRoundStarted() &&
        this.isRoundDeadlineExpired() &&
        this.hasOpponentNotStarted()
      ) {
        return RoomStatus.OpponentToStart;
      } else if (
        this.isRoundStarted() &&
        this.isRoundDeadlineExpired() &&
        this.hasOpponentStarted() &&
        this.hasOpponentRoundExpired()
      ) {
        return RoomStatus.Cancelled;
      } else if (
        this.isRoundStarted() &&
        this.isRoundDeadlineExpired() &&
        this.hasOpponentStarted() &&
        !this.hasOpponentRoundExpired()
      ) {
        return RoomStatus.OpponentToFinish;
      } else if (
        this.isRoundStarted() &&
        this.isRoundDeadlineExpired() &&
        this.hasOpponentFinished()
      ) {
        return RoomStatus.Lost;
      } else if (
        this.isRoundFinished() &&
        this.hasOpponentNotStarted() &&
        !this.isStartRoundDeadlineExpired()
      ) {
        return RoomStatus.OpponentToStart;
      } else if (
        this.isRoundFinished() &&
        this.hasOpponentNotStarted() &&
        this.isStartRoundDeadlineExpired()
      ) {
        return RoomStatus.Cancelled;
      } else if (
        this.isRoundFinished() &&
        this.hasOpponentStarted() &&
        !this.hasOpponentRoundExpired()
      ) {
        return RoomStatus.OpponentToFinish;
      } else if (
        this.isRoundFinished() &&
        this.hasOpponentStarted() &&
        this.hasOpponentRoundExpired()
      ) {
        return RoomStatus.Won;
      } else if (
        !this.isStartRoundDeadlineExpired() &&
        this.isRoundNotStarted() &&
        this.hasOpponentFinished()
      ) {
        return RoomStatus.Start;
      }
    }
    console.error(`Cannot determine status for room ${this.id}`);
    return RoomStatus.Cancelled;
  }

  deadline(): number {
    switch (this.status()) {
      case RoomStatus.Cancelled:
      case RoomStatus.Draw:
      case RoomStatus.Lost:
      case RoomStatus.Won:
        return this.model.sortDate;
      case RoomStatus.Open:
        return this.model.joinDeadline; // todo wrong
      case RoomStatus.Start:
        return this.model.startRoundDeadline;
      case RoomStatus.Finish:
        if (!this.model.roundDeadline) {
          throw new RoomError('Expected a roundDeadline');
        }
        return this.model.roundDeadline;
      case RoomStatus.OpponentToStart:
        return this.model.startRoundDeadline;
      case RoomStatus.OpponentToFinish:
        if (!this.model.opponent?.roundDeadline) {
          throw new RoomError('Expected an opponent to have a roundDeadline');
        }
        return this.model.opponent?.roundDeadline;
      case RoomStatus.Processing:
        return this.model.roundDeadline ?? Date.now() - 1;
      default:
        throw new RoomError(`Unsupported RoomStatus: ${this.status()}`);
    }
  }

  score(): number | undefined {
    return this.model.score;
  }

  opponentScore(): number | undefined {
    return this.model.opponent?.score;
  }

  isProcessing(): boolean {
    if (
      isNullOrUndefined(this.model.roundDeadline) ||
      isNullOrUndefined(this.model.opponent) ||
      isNullOrUndefined(this.model.opponent?.roundDeadline)
    ) {
      return false;
    }

    if (!this.model.opponent?.roundDeadline) {
      return false;
    }

    const currentTime = Date.now();
    if (
      currentTime < this.model.roundDeadline ||
      currentTime < this.model.opponent?.roundDeadline
    ) {
      return false;
    }

    if (this.isInProgress()) {
      // It can take 60 seconds for the room to finish processing
      const processingBuffer = 60000;
      const time = Date.now() - processingBuffer;
      if (time < this.model.roundDeadline || time < this.model.opponent?.roundDeadline) {
        return true;
      }
    }
    return false;
  }

  isRoundNotStarted(): boolean {
    return this.model.roundState === Round.NotStarted;
  }

  isRoundStarted(): boolean {
    return this.model.roundState === Round.Started;
  }

  isRoundFinished(): boolean {
    return this.model.roundState === Round.Finished;
  }

  startRoundDeadline(): number {
    return this.model.startRoundDeadline;
  }

  invoice(): string | undefined {
    return this.model.opponent?.invoice;
  }

  hasOpponent(): boolean {
    return this.model.opponent?.nick !== undefined;
  }

  opponent(): string | undefined {
    return this.model.opponent?.nick;
  }

  opponentId(): string | undefined {
    return this.model.opponent?.userId ?? undefined;
  }

  bountyAmount(): number | undefined {
    return this.model.bounty?.amount;
  }

  bountyJackpot(): boolean | undefined {
    return this.model.bounty?.jackpot;
  }

  currency(): Currency {
    if (this.model.currency === 'USD') {
      return Currency.USD;
    } else if (this.model.currency === 'MCN') {
      return Currency.MCN;
    }
    return Currency.SAT;
  }

  entryFee(): number {
    return this.model.entryFee;
  }

  formattedEntryFee(currencyPipe: CurrencyPipe) {
    if (this.currency() === Currency.USD) {
      return currencyPipe.transform(this.entryFee() / 100, 'USD', 'symbol', '1.2-2');
    } else if (this.currency() === Currency.MCN) {
      return `${this.entryFee().toString()}`;
    }
    return `${this.entryFee().toString()} sats`;
  }

  isMatch(): boolean {
    return this.model.creationMethod === 'MATCH';
  }

  reward(): number {
    return this.model.reward;
  }

  formattedReward(currencyPipe: CurrencyPipe) {
    return amountForCurrency(this.currency(), this.reward(), currencyPipe);
  }

  formattedFee(currencyPipe: CurrencyPipe) {
    return amountForCurrency(this.currency(), this.entryFee(), currencyPipe);
  }

  sortDate(): number {
    return this.model.sortDate;
  }

  result(): RoomDisplayResult {
    return this.model.result;
  }

  isWon(): boolean {
    return this.result() === RoomDisplayResult.Win;
  }

  isLost(): boolean {
    return this.result() === RoomDisplayResult.Lose;
  }

  isDraw(): boolean {
    return this.result() === RoomDisplayResult.Draw;
  }

  isCancelled(): boolean {
    return this.result() === RoomDisplayResult.Cancelled;
  }

  isInProgress(): boolean {
    return this.result() === RoomDisplayResult.InProgress;
  }

  isExpired(): boolean {
    return this.model.joinDeadline < Date.now();
  }

  isStartRoundDeadlineExpired(): boolean {
    return this.model.startRoundDeadline < Date.now();
  }

  isRoundDeadlineExpired(): boolean {
    return (this.model.roundDeadline ?? 0) < Date.now();
  }

  hasOpponentNotStarted(): boolean {
    return this.model.opponent?.roundState === Round.NotStarted;
  }

  hasOpponentStarted(): boolean {
    return this.model.opponent?.roundState === Round.Started;
  }

  hasOpponentFinished(): boolean {
    return this.model.opponent?.roundState === Round.Finished;
  }

  hasOpponentRoundExpired(): boolean {
    if (this.model.opponent?.roundDeadline === undefined) {
      throw new RoomError('Round deadline does not exist!');
    }
    return this.model.opponent.roundDeadline < Date.now();
  }

  isFinished(): boolean {
    switch (this.result()) {
      case RoomDisplayResult.Cancelled:
      case RoomDisplayResult.Draw:
      case RoomDisplayResult.Lose:
      case RoomDisplayResult.Win:
        return true;
    }
    return false;
  }

  static statusColor(status: RoomStatus): string {
    switch (status) {
      case RoomStatus.Cancelled:
        return '--game-refund';
      case RoomStatus.Draw:
        return '--game-draw';
      case RoomStatus.Lost:
        return '--game-lose';
      case RoomStatus.Won:
        return '--game-win';
      case RoomStatus.Open:
        return '--yellow-500';
      case RoomStatus.Start:
      case RoomStatus.Finish:
        return '--yellow-500';
      case RoomStatus.OpponentToStart:
      case RoomStatus.OpponentToFinish:
      case RoomStatus.Processing:
        return '--blue-500';
    }
  }

  amountAscii(): string | undefined {
    const status = this.status();
    switch (status) {
      case RoomStatus.Cancelled:
      case RoomStatus.Draw:
        return '⏎';
      case RoomStatus.Lost:
        return '-';
      case RoomStatus.Won:
        return '+';
      default:
        return undefined;
    }
  }

  formattedStatusAmount(currencyPipe: CurrencyPipe) {
    switch (this.status()) {
      case RoomStatus.Cancelled:
      case RoomStatus.Draw:
      case RoomStatus.Lost:
        return this.formattedEntryFee(currencyPipe);
      case RoomStatus.Won:
      case RoomStatus.Open:
      case RoomStatus.Start:
      case RoomStatus.Finish:
      case RoomStatus.OpponentToStart:
      case RoomStatus.OpponentToFinish:
        return this.formattedReward(currencyPipe);
      case RoomStatus.Processing:
      default:
        return this.formattedReward(currencyPipe);
    }
  }

  statusText(translocoService: TranslocoService): string {
    const map: Record<RoomStatus, string> = {
      [RoomStatus.Cancelled]: 'shared.room.status-returned',
      [RoomStatus.Draw]: 'shared.room.status-draw',
      [RoomStatus.Lost]: 'shared.room.status-lost',
      [RoomStatus.Won]: 'shared.room.status-won',
      [RoomStatus.Open]: 'shared.room.status-to-win',
      [RoomStatus.Start]: 'shared.room.status-to-win',
      [RoomStatus.Finish]: 'shared.room.status-to-win',
      [RoomStatus.OpponentToStart]: 'shared.room.status-to-win',
      [RoomStatus.OpponentToFinish]: 'shared.room.status-to-win',
      [RoomStatus.Processing]: 'shared.room.status-processing',
    };

    const key = map[this.status()];
    if (!key) {
      console.error('unsupported room status');
      return '';
    }

    return translocoService.translate(key);
  }

  gameTitle(translocoService: TranslocoService) {
    const map: Record<GameId, string> = {
      [GameId.Solitaire]: 'games.solitaire-name',
      [GameId.Blocks]: 'games.blocks-name',
      [GameId.Blackjack]: 'games.blackjack-name',
      [GameId.TwentyOne]: 'games.twentyone-name',
    };

    const key = map[this.gameId()];
    if (!key) {
      return 'Solitaire';
    }

    return translocoService.translate(key);
  }

  title(translocoService: TranslocoService): string {
    const opponentParams = () => ({ opponent: this.opponent() });

    const map: Record<RoomStatus, () => { key: string; params?: HashMap }> = {
      [RoomStatus.Cancelled]: () => ({ key: 'shared.room.title-cancelled' }),
      [RoomStatus.Draw]: () => ({ key: 'shared.room.title-draw', params: opponentParams() }),
      [RoomStatus.Lost]: () => ({ key: 'shared.room.title-lost', params: opponentParams() }),
      [RoomStatus.Won]: () => ({ key: 'shared.room.title-won', params: opponentParams() }),
      [RoomStatus.Open]: () => ({ key: 'shared.room.title-open' }),
      [RoomStatus.Start]: () =>
        this.isMatch()
          ? { key: 'shared.room.title-start-matched' }
          : { key: 'shared.room.title-start-opponent-matched', params: opponentParams() },
      [RoomStatus.Finish]: () =>
        this.hasOpponent()
          ? { key: 'shared.room.title-finish', params: opponentParams() }
          : { key: 'shared.room.title-finish-no-opponent' },
      [RoomStatus.OpponentToStart]: () => ({
        key: 'shared.room.title-opponent-start',
        params: opponentParams(),
      }),
      [RoomStatus.OpponentToFinish]: () => ({
        key: 'shared.room.title-opponent-finish',
        params: opponentParams(),
      }),
      [RoomStatus.Processing]: () => ({ key: 'shared.room.title-processing' }),
    };

    const translationData = map[this.status()]?.() || { key: '' };
    return translocoService.translate(translationData.key, translationData.params);
  }

  resultStatus(): RoomResultStatus {
    const status = this.status();
    if (status === RoomStatus.Open) {
      return RoomResultStatus.InPlay;
    } else if (status === RoomStatus.OpponentToStart || status === RoomStatus.OpponentToFinish) {
      return RoomResultStatus.InPlay;
    } else if (
      status === RoomStatus.Cancelled ||
      status === RoomStatus.Draw ||
      status === RoomStatus.Lost ||
      status === RoomStatus.Won
    ) {
      return RoomResultStatus.Result;
    } else if (status === RoomStatus.Processing) {
      return RoomResultStatus.Processing;
    } else if (status === RoomStatus.Finish || status === RoomStatus.Start) {
      return RoomResultStatus.Game;
    }
    throw new Error('Unsupported RoomResultStatus for ' + status);
  }
}
